import React, { useEffect } from 'react';
import styled from 'styled-components';
import katex from 'katex';
import { CloseOutlined, ExclamationOutlined } from '@ant-design/icons';
import { DateTime, Duration } from 'luxon';

import * as unitMagic from '@metservicedev/cf-units';
import { NcoJSONRAW } from '@metservicedev/platforms-services-interface/src/model/NcoJSON';
import { getDatabilityIndex } from '@metservicedev/platforms-services-interface/src';
import { GetCfjsonResponse } from '@metservicedev/platforms-services-interface/src/services/movIntegrationMDS/MovIntegrationMDSModel';

import { CfjsonRow } from '@mono/types/src/weather-widgets/threat-matrix';
import { Capability } from '@mono/types/src/mds';
import { getIcon } from '@mono/ww-threat-matrix-icons';

import { Cell } from './types';
import { generateThresholdStatus, getThresholdOperator } from './thresholds';
import { TextStyles } from '@mono/metui/src/utils/text-styles-selector';

interface CfjsonRowHeader {
    row: CfjsonRow;
    mode: 'cozy' | 'compact';
}

const RowLabel = styled.span<{ mode?: 'cozy' | 'compact' }>`
    flex: 1;
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p}
`;

const ThresholdLabel = styled.span<{ mode?: 'cozy' | 'compact' }>`
    width: ${({ mode }) => (mode === 'cozy' ? '70px' : '50px')};
    ${({ mode }) => TextStyles[mode ?? 'cozy'].p};
`;

export const CfjsonRowHeader = ({ row, mode }: CfjsonRowHeader) => {
    const Icon = getIcon(row.icon);
    let KatexInner = <span />;

    try {
        KatexInner =
            row.type === 'cfjson' && row.unitLabel ? (
                <KatexSpan>
                    (<span dangerouslySetInnerHTML={{ __html: katex.renderToString(row.unitLabel || '') }} />)
                </KatexSpan>
            ) : (
                <span />
            );
    } catch (error) {
        console.warn(error);
    }
    const thresholdOperator = getThresholdOperator(row.thresholdOperator);
    return (
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', paddingRight: 8, gap: 8, width: '100%' }}>
            {Icon && <Icon size={TextStyles[mode ?? 'cozy'].iconSize} />}
            <RowLabel mode={mode}>
                {row.label} {KatexInner}
            </RowLabel>

            <div style={{ display: 'flex', alignItems: 'center' }}>
                <ExclamationOutlined style={{ color: ' #EE7734', fontSize: 'large' }} />
                <ThresholdLabel mode={mode}>
                    <span style={{ marginLeft: 2.5, marginRight: 2.5 }}>{thresholdOperator}</span>
                    <span>{row.warningThreshold}</span>
                </ThresholdLabel>
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <CloseOutlined style={{ color: '#D62828', fontSize: 'large' }} />
                <ThresholdLabel mode={mode}>
                    <span style={{ marginLeft: 2.5, marginRight: 2.5 }}>{thresholdOperator}</span>
                    <span>{row.alertThreshold}</span>
                </ThresholdLabel>
            </div>
        </div>
    );
};

const KatexSpan = styled.span`
    align-self: stretch;
    .katex .mathnormal {
        font-size: 14px;
        font-style: normal !important;
        font-family: 'Open sans' !important;
    }
`;

interface CellsForRowProps {
    timeRange: string;
    cfJsonsResponses?: GetCfjsonResponse[];
    capabilities: Capability[];
    now: string;
    row: CfjsonRow;
}

export const generateCellsForCfjson = ({ now: _now, timeRange, cfJsonsResponses, capabilities, row }: CellsForRowProps): Cell[] => {
    const now = DateTime.fromISO(_now).toUTC().startOf('hour');

    const [earliest, latest] = timeRange.split('--').map((d) => now.plus(Duration.fromISO(d)));
    const maximumCellWidth = latest.diff(earliest, 'hours').as('hours');

    const rowDatabilityIndex = getDatabilityIndex({
        capabilityId: row.capabilityId,
        datasetId: row.datasetId,
        timeRange,
        id: row.id,
        space: { type: 'Point', coordinates: [row.space.lon, row.space.lat] },
    });
    const cfJson = cfJsonsResponses?.find((cfjsonResponse) => cfjsonResponse.databilityIndex === rowDatabilityIndex)?.cfjson ?? undefined;

    if (!cfJson)
        return [
            {
                status: undefined,
                label: undefined,
                width: maximumCellWidth,
            },
        ];

    const dataTimeStep = DateTime.fromISO(cfJson.variables.time.data[1]).diff(DateTime.fromISO(cfJson.variables.time.data[0])).as('hours');

    const firstGreaterIndex = cfJson.variables.time.data.findIndex((t) => t > now.toISO());
    const initialWidth = DateTime.fromISO(cfJson.variables.time.data[firstGreaterIndex]).diff(now.startOf('hour')).as('hours');

    const reduceDefault = cfJson.variables.time.data.find((t) => t === now.startOf('hour').toISO({ suppressMilliseconds: true }))
        ? {
              x: 0,
              cells: [],
          }
        : {
              x: initialWidth,
              cells: [
                  {
                      status: generateThresholdStatus({
                          thresholds: [{ value: getCapabilityData(cfJson, row.capabilityId, capabilities, firstGreaterIndex - 1, row.unit), ...row }],
                      }),
                      label: getCapabilityData(cfJson, row.capabilityId, capabilities, firstGreaterIndex - 1, row.unit),
                      width: initialWidth,
                  },
              ],
          };

    const times = cfJson.variables.time.data.filter((t) => {
        if (t < now.toISO()) return false;
        if (t >= latest.toISO()) return false;
        return true;
    });

    return times.reduce((acc, time, i) => {
        const { x, cells } = acc;
        const width = x + dataTimeStep > maximumCellWidth ? maximumCellWidth - x : dataTimeStep;

        const timeIndex = findTimeIndexForCellTime(time, cfJson, dataTimeStep);
        const value = timeIndex ? getCapabilityData(cfJson, row.capabilityId, capabilities, timeIndex, row.unit) : undefined;

        if (x >= maximumCellWidth) return acc;
        return {
            x: x + width,
            cells: [
                ...cells,
                {
                    status: generateThresholdStatus({ thresholds: [{ value, ...row }] }),
                    label: value,
                    width,
                },
            ],
        };
    }, reduceDefault).cells;
};

const getCapabilityData = (cfJson: NcoJSONRAW, capabilityId: string | undefined, capabilities: Capability[], timeIndex: number, unit?: string) => {
    if (!cfJson) return;

    // Find the capability in the `capabilities` array that has a matching ID.
    const rowCapability = capabilities.find((capability) => capability.id === capabilityId);

    if (!rowCapability) return;

    // Find the name of the first variable in the capability that is not an attribute.
    const capabilityVarName = Object.keys(rowCapability.variables).find((name) => name !== 'attributes');

    if (!capabilityVarName) {
        console.warn(`can't find variable name for capabilityId : ${capabilityId}`);
        return;
    }
    // If the cfJson has the variable with the found name, return the value at the specified time index.
    if (cfJson.variables.hasOwnProperty(capabilityVarName)) {
        const value = cfJson.variables[capabilityVarName].data[timeIndex];
        if (!unit) return value;
        try {
            const convertedUnit = unitMagic.convertValueUnit({ from: cfJson.variables[capabilityVarName].attributes?.units, to: unit, value: value });
            return convertedUnit;
        } catch (e) {
            console.warn(`can't convert unit for capabilityId : ${capabilityId}`);
            return value;
        }
    }
};

const findTimeIndexForCellTime = (cellTime: string, cfJson: NcoJSONRAW, timeStep: number) => {
    const perfectMatch = cfJson.variables.time.data.findIndex((time: string) => time === cellTime);

    // If there is a perfect match, return that
    if (perfectMatch !== -1) return perfectMatch;

    // Otherwise, find the closest match to the provided cellTime.
    const closestTime = cfJson.variables.time.data.reduce<string>((closest: string, time: string) => {
        // Calculate the difference in hours between the current time and the cellTime.
        const closestDiff = DateTime.fromISO(closest).diff(DateTime.fromISO(cellTime), 'hours').hours;
        const timeDiff = DateTime.fromISO(time).diff(DateTime.fromISO(cellTime), 'hours').hours;

        // If the difference in hours for the current time is less than that of the current closest time, return the current time.
        if (Math.abs(timeDiff) < Math.abs(closestDiff)) return time;

        // Otherwise, return the current closest time.
        return closest;
    }, '2420-01-05T22:51:13.439Z'); // random date in the future so that the first time diff is always less than the closest diff

    const closestIndex = cfJson.variables.time.data.findIndex((time: string) => time === closestTime);
    if (closestIndex <= timeStep) {
        return closestIndex;
    }
    return undefined;
};

interface CfjsonRowBodyProps {
    row: CfjsonRow;
    cfJsonsResponses?: GetCfjsonResponse[];
    capabilities: Capability[];
}
export const generateObsCellForCfjson = (props: CfjsonRowBodyProps) => {
    const { row, cfJsonsResponses, capabilities } = props;

    const noObsCell: Cell = {
        status: undefined,
        label: undefined,
        width: 1,
        isObs: true,
    };
    if (!row.obs) return noObsCell;
    if (!row.obs.capabilityId) return noObsCell;

    const obsDatabilityindex = getDatabilityIndex({
        capabilityId: row.obs.capabilityId,
        datasetId: row.obs.id,
        timeRange: 'PT-1H--PT0S',
        id: row.obs.id + '-obs',
        space: { type: 'Point', coordinates: [row.space.lon, row.space.lat] },
    });

    const obsCfjson = cfJsonsResponses?.find((cfjsonResponse) => cfjsonResponse.databilityIndex === obsDatabilityindex)?.cfjson ?? undefined;
    if (!obsCfjson) return noObsCell;
    const capability = capabilities.find((c) => c.id === row.obs?.capabilityId);
    if (!capability) return noObsCell;

    const variableKey = Object.keys(capability.variables)[0]; // This assumes that the capability only has one variable - which most should, but vectors will not (north and east)
    const data = Object.values(obsCfjson.variables).find((v) => v.attributes?.standard_name === variableKey)?.data;
    if (!data) return noObsCell;
    const mostRecentTimeIndex = obsCfjson.variables.time.data.reduce((recentestTimeIndex, time, index, times) => {
        if (time > times[recentestTimeIndex]) return index;
        return recentestTimeIndex;
    }, 0);
    const datum = data[mostRecentTimeIndex];

    const obsCell: Cell = {
        status: generateThresholdStatus({ thresholds: [{ value: datum, ...row }] }),
        label: undefined,
        width: 1,
        isObs: true,
    };
    return obsCell;
};
